import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, ImageOff } from "lucide-react";
import { fotoUrl } from "@/lib/publicApi";

// Galeria pública com foto principal grande + miniaturas + lightbox simples.
// Props: fotos = [{id, principal, ordem}], alt = string
export function FotoGallery({ fotos = [], alt = "Foto", testid = "foto-gallery" }) {
  const list = Array.isArray(fotos) ? fotos : [];
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);

  const safeIdx = Math.min(Math.max(idx, 0), Math.max(list.length - 1, 0));
  const current = list[safeIdx];

  const prev = useCallback(() => {
    setIdx((i) => (i <= 0 ? Math.max(list.length - 1, 0) : i - 1));
  }, [list.length]);
  const next = useCallback(() => {
    setIdx((i) => (i >= list.length - 1 ? 0 : i + 1));
  }, [list.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  if (list.length === 0) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="aspect-[16/10] w-full rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center text-stone-400"
      >
        <div className="flex flex-col items-center gap-2">
          <ImageOff size={32} strokeWidth={1.5} />
          <span className="text-xs">Fotos em breve</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testid} className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative block w-full aspect-[16/10] rounded-lg overflow-hidden bg-stone-100 border border-stone-200 group"
        aria-label="Ampliar foto"
        data-testid={`${testid}-main`}
      >
        <img
          src={fotoUrl(current.id)}
          alt={alt}
          loading="eager"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
        {list.length > 1 && (
          <>
            <span
              onClick={(e) => { e.stopPropagation(); prev(); }}
              role="button"
              tabIndex={0}
              aria-label="Foto anterior"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-sm"
            >
              <ChevronLeft size={20} />
            </span>
            <span
              onClick={(e) => { e.stopPropagation(); next(); }}
              role="button"
              tabIndex={0}
              aria-label="Próxima foto"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-sm"
            >
              <ChevronRight size={20} />
            </span>
            <span className="absolute bottom-3 right-3 text-[11px] uppercase tracking-widest bg-black/50 text-white px-2.5 py-1 rounded-full">
              {safeIdx + 1}/{list.length}
            </span>
          </>
        )}
      </button>

      {list.length > 1 && (
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2">
          {list.map((f, i) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`${testid}-thumb-${i}`}
              aria-label={`Ver foto ${i + 1}`}
              className={`relative aspect-square rounded-md overflow-hidden border transition-all ${
                i === safeIdx
                  ? "border-[#083A6B] ring-2 ring-[#083A6B]/30"
                  : "border-stone-200 opacity-80 hover:opacity-100"
              }`}
            >
              <img
                src={fotoUrl(f.id)}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid={`${testid}-lightbox`}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Fechar"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <X size={20} />
          </button>
          {list.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Foto anterior"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <img
            src={fotoUrl(current.id)}
            alt={alt}
            className="max-h-[85vh] max-w-[92vw] object-contain rounded-md"
            onClick={(e) => e.stopPropagation()}
          />
          {list.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Próxima foto"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
